import { Component, OnInit, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FirebaseService } from '../../services/firebase.service';
import { CustomerdatabaseService } from '../../services/customerdatabase.service';
import { UpdateNotification } from './update-notification.interface';

@Component({
  selector: 'app-important-messages-modal',
  templateUrl: './important-messages-modal.component.html',
  styleUrls: ['./important-messages-modal.component.scss'],
})
export class ImportantMessagesModalComponent implements OnInit {

  messages: UpdateNotification[] = [];
  loading: boolean = true;
  @Input() autoOpened: boolean = false; // Track if opened automatically on login
  @Input() externalMessages: UpdateNotification[] = []; // For preview mode

  constructor(
    private modalController: ModalController,
    private firebaseService: FirebaseService,
    private customerDB: CustomerdatabaseService
  ) { }

  async ngOnInit() {
    console.log('ImportantMessagesModalComponent ngOnInit');
    console.log('External messages:', this.externalMessages);
    console.log('Auto opened:', this.autoOpened);

    // If external messages are provided (for preview), use them directly
    if (this.externalMessages && this.externalMessages.length > 0) {
      console.log('Using external messages for preview');
      this.messages = this.externalMessages;
      this.loading = false;
      console.log('Messages set:', this.messages);
      return;
    }

    console.log('Loading messages from Firebase');
    // Otherwise load messages from Firebase
    await this.loadMessages();
  }

  async loadMessages() {
    try {
      this.loading = true;

      // Load important messages from Firebase
      try {
        console.log('Attempting to load important messages from Firebase...');

        // First try with ordering by timestamp
        let messagesSnapshot;
        try {
          messagesSnapshot = await this.firebaseService.getImportantMessages()
            .orderBy('timestamp', 'desc')
            .limit(1)
            .get();
        } catch (orderError) {
          console.warn('Could not order by timestamp, trying without ordering:', orderError);
          // If ordering fails, try without ordering (in case timestamp field doesn't exist)
          messagesSnapshot = await this.firebaseService.getImportantMessages()
            .limit(1)
            .get();
        }

        console.log('Messages snapshot received:', messagesSnapshot);
        console.log('Messages snapshot empty:', messagesSnapshot.empty);
        console.log('Number of documents:', messagesSnapshot.docs.length);

        if (!messagesSnapshot.empty) {
          console.log('Processing messages...');
          const latestMessage = messagesSnapshot.docs[0];
          const data = latestMessage.data();
          console.log('Processing document:', latestMessage.id, data);

          // Check if message should be displayed based on date and time
          const messageDate = data.timestamp ?
            (data.timestamp.toDate ? data.timestamp.toDate() : new Date(data.timestamp)) :
            new Date();

          const currentDate = new Date();

          console.log('Message timestamp:', messageDate);
          console.log('Current timestamp:', currentDate);

          // Check if message should be displayed based on start and end time
          const startTime = data.startTime ?
            (data.startTime.toDate ? data.startTime.toDate() : new Date(data.startTime)) :
            messageDate; // Fallback to timestamp if no startTime

          const endTime = data.endTime ?
            (data.endTime.toDate ? data.endTime.toDate() : new Date(data.endTime)) :
            null; // No end time means message runs indefinitely

          console.log('Start time:', startTime);
          console.log('End time:', endTime);
          console.log('Current time:', currentDate);

          // Check if current time is within the display window
          const isAfterStart = currentDate >= startTime;
          const isBeforeEnd = !endTime || currentDate <= endTime;
          if (isAfterStart && isBeforeEnd) {
            this.messages = [{
              id: latestMessage.id,
              title: 'Important Message',
              description: data.message || data.text || data.content || '',
              createdAt: messageDate,
              mediaType: data.mediaType || null,
              mediaUrl: data.mediaUrl || null,
              category: data.category || 'info', // Default to info if no category
              startTime: startTime,
              endTime: endTime
            } as UpdateNotification];
            console.log('Message will be displayed:', this.messages[0]);
          } else {
            console.log('Message is outside display window, not displaying');
            this.messages = [];
          }
          return;
        } else {
          console.log('No messages found in Firebase collection');
        }
      } catch (firebaseError) {
        console.error('Error loading Firebase messages:', firebaseError);
        console.error('Error details:', firebaseError.message);
      }

      // Fallback: Show default welcome message
      console.log('Showing default welcome message');
      this.messages = [{
        id: 'default-welcome',
        title: 'Welcome to MONA AI Platform!',
        description: 'Thank you for using our platform. Important updates and announcements will appear here.',
        createdAt: new Date(),
        mediaType: null,
        mediaUrl: null,
        category: 'info'
      }];
    } catch (error) {
      console.error('Error loading important messages:', error);
      this.messages = [];
    } finally {
      this.loading = false;
    }
  }

  closeModal() {
    // If auto-opened on login, mark as seen in localStorage
    if (this.autoOpened) {
      this.markMessagesAsSeen();
    }
    this.modalController.dismiss();
  }

  private markMessagesAsSeen() {
    const latestMessageId = this.messages.length > 0 ? this.messages[0].id : null;
    if (latestMessageId) {
      localStorage.setItem('lastSeenImportantMessage', latestMessageId);
    }
  }

  formatDate(date: any): Date {
    if (date?.toDate) {
      return date.toDate();
    }
    return new Date(date);
  }

  getCategoryIcon(category: string | undefined): string {
    switch (category) {
      case 'success':
        return 'checkmark-circle-outline';
      case 'warning':
        return 'warning-outline';
      case 'danger':
        return 'alert-circle-outline';
      case 'info':
      default:
        return 'information-circle-outline';
    }
  }

  // Note: hasNewMessages functionality moved to dashboard.page.ts as instance method
} 